import { Button } from "@pma/components/common/button"
import { Input } from "@pma/components/common/input"
import useTranslator from "@pma/hooks/use-translator"
import * as React from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { GroupedTasksContext } from "./context/GroupedTasksProvider"
import { IGroupUpdateInput } from "./api-fns"
import { useParams } from "react-router-dom"

export interface IUpdateGroupProps {
  data: any
  setSelectedGroupToEdit: React.Dispatch<any>
}
interface IFormInputs {
  group_name: string
  group_id: string
}

const schema = z.object({
  group_name: z.string().trim().min(1, "This field is required."),
})

export function UpdateGroup({ data, setSelectedGroupToEdit }: IUpdateGroupProps) {
  const { translate } = useTranslator()
  const { projectId } = useParams()
  const { updateGroupMutation, groupedTasksRefetch } = React.useContext(GroupedTasksContext)
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    reset,
  } = useForm<IFormInputs>({
    resolver: zodResolver(schema),
    values: {
      group_id: data?.id,
      group_name: data?.name,
    },
  })

  const onSubmit: SubmitHandler<IFormInputs> = (_data) => {
    if (!projectId) return
    const payload: IGroupUpdateInput = {
      ..._data,
      group_id: parseInt(data?.id),
    }
    updateGroupMutation?.mutate(payload)
  }

  React.useEffect(() => {
    if (!updateGroupMutation?.isPending && updateGroupMutation?.isSuccess) {
      groupedTasksRefetch()
      reset()
      updateGroupMutation?.reset()
      setSelectedGroupToEdit(null)
    }
  }, [updateGroupMutation?.isPending])

  const handleCancel = () => {
    reset()
    setSelectedGroupToEdit(null)
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className='mb-3 ml-3 flex flex-wrap gap-2'>
        <div className='flex flex-col gap-1'>
          <Input
            placeholder={translate("New Group")}
            className='mr-3 h-9 sm:w-[100px] lg:w-[500px]'
            {...register("group_name", { required: true })}
            onKeyDown={(e) => {
              if (e.code == "Space") {
                setValue("group_name", `${watch("group_name")} `)
              }
            }}
          />
          <span className='text-destructive'>
            {errors.group_name && <span>{errors.group_name.message}</span>}
          </span>
        </div>
        <div className='flex gap-1'>
          <Button
            size={"sm"}
            loading={updateGroupMutation?.isPending || isSubmitting}
            disabled={updateGroupMutation?.isPending || !isValid}
            type='submit'
          >
            {translate("Update Group")}
          </Button>
          <Button
            size={"sm"}
            loading={updateGroupMutation?.isPending || isSubmitting}
            disabled={updateGroupMutation?.isPending || !isValid}
            type='button'
            variant='outline'
            onClick={handleCancel}
          >
            {translate("Cancel")}
          </Button>
        </div>
      </form>
    </>
  )
}
